import type { TranslationKeys } from './ar';

export const en: TranslationKeys = {
  nav: {
    aboutUs: 'About Us',
    services: 'Our Services',
    specialties: 'Medical Specialties',
    doctors: 'Our Doctors',
    faq: 'FAQs',
    contact: 'Contact us',
  },
  auth: {
    doctorLogin: 'Doctor Login',
    patientLogin: 'Patient Login',
  },
  hero: {
    title: 'Takassusi Consult',
    subtitle: 'The Trusted Choice for Expert Medical Consultations.',
    description:
      'A secure digital health platform, by Takassusi General Limited Company, a subsidiary of King Faisal Specialist Hospital Holding Company. The platform connects King Faisal Specialist Hospital consultants with patients, and physicians who require specialized video consultations or trusted second opinions for complex medical conditions, empowering confident and well-informed clinical decisions.',
    ctaButton: 'Register Now',
  },
  about: {
    title: 'About Us',
    description:
      'A secure digital health platform, by Takassusi General Limited Company, a subsidiary of King Faisal Specialist Hospital Holding Company. The platform connects King Faisal Specialist Hospital consultants with patients, and physicians who require specialized video consultations or trusted second opinions for complex medical conditions, empowering confident and well-informed clinical decisions.',
    vision: {
      title: 'Our Vision',
      description:
        'We aim to empower patients and healthcare providers with access to medical opinions that meet the standards of leading medical institutions, and to make "Takassusi Consult" a trusted resource for evaluating complex cases, rare diseases, and providing highly accurate second medical opinions.',
      iconAlt: 'Our Vision',
    },
    mission: {
      title: 'Our Mission',
      description:
        'Providing supportive medical opinions and consultations through an advanced digital platform that connects patients with specialist consultants, aiming to clarify uncertain diagnoses and reinforce medical decisions based on evidence and professional expertise, without replacing direct clinical examination when necessary.',
      iconAlt: 'Our Mission',
    },
  },
  services: {
    title: 'Our Services',
    disclaimer:
      'All services provided are advisory and supportive. They do not replace direct clinical examination.',
    videoConsultation: {
      title: 'Video Medical Consultations',
      description:
        'Specialized medical consultations via secure, encrypted video calls, delivered by certified consultants in specific specialties.',
      features: [
        'Direct medical case discussion with certified consultants.',
        'Clarifying possible diagnoses and explaining available treatment options.',
        'Answering questions from the patient or treating physician to support medical decision-making.',
      ],
    },
    secondOpinion: {
      title: 'Second Medical Opinion',
      description:
        'Enables patients and physicians to obtain an additional medical evaluation for complicated cases by a specialist consultant.',
      features: [
        'Additional medical evaluation for complex or unclear cases.',
        'Review of medical reports, test results, and radiology images.',
        'Supportive analytical opinion focusing on explaining the condition and clarifying possible alternatives.',
      ],
    },
  },
  specialties: {
    title: 'Medical Specialties',
    disclaimer:
      'All recommendations provided are intended as supportive advice and are aimed at supporting medical decision-making.',
    items: [
      {
        title: 'Oncology',
        description:
          'Our oncology consultants provide accurate assessments of various types of cancer, with in-depth reviews of reports and proposed treatment plans guided by the latest research.',
      },
      {
        title: 'Cardiovascular Diseases',
        description:
          'Specialized evaluation of critical and chronic cardiac conditions, including heart failure, arrhythmia, and arterial disease, with careful review of cardiac tests and reports.',
      },
      {
        title: 'Neurology',
        description:
          'Comprehensive evaluation of complex neurological disorders, such as strokes, epilepsy, multiple sclerosis, neuropathies, and movement disorders, with accurate interpretation of neurological images and reports.',
      },
      {
        title: 'Organ Transplantation and Post-Transplant Care',
        description:
          'Assessing cases eligible for organ transplantation, following up on post-transplantation cases, and reading analyses and immunological compatibility with high accuracy, based on our leading professional expertise.',
      },
      {
        title: 'Rare Diseases',
        description:
          'Accurate assessments of rare and difficult-to-diagnose conditions, based on the latest global medical references, guide patients in understanding their condition and choosing the most appropriate treatment plan.',
      },
      {
        title: 'Genetic Diseases',
        description:
          'Providing interpretation of advanced genetic testing and explaining genetic associations related to chronic and rare diseases, with recommendations for additional testing, follow-up, and genetic consultations when necessary.',
      },
    ],
  },
  whyUs: {
    title: 'Why "Takassusi Consult"?',
    subtitle:
      'Choosing Takassusi Consult platform means accessing the expertise of King Faisal Specialist Hospital wherever you are.',
    features: [
      {
        title: 'Secure Video Consultations',
        description:
          'Consultations in precise medical specialties via encrypted calls with certified consultants.',
      },
      {
        title: 'Trusted Second Medical Opinions',
        description:
          'Provided by King Faisal Specialist Hospital consultants with deep expertise.',
      },
      {
        title: 'Expertise in Complicated Cases',
        description:
          'Accurate assessments of rare and complex cases based on the latest medical references.',
      },
      {
        title: 'Unlimited Access',
        description:
          'The service is available within the Kingdom of Saudi Arabia and internationally.',
      },
      {
        title: 'Protect Your Data',
        description:
          'Commitment to local and international health data protection standards.',
      },
    ],
  },
  howItWorks: {
    title: 'How does the "Takassusi Consult" platform work?',
    videoConsultation: {
      title: 'First: Request for Medical Consultation (Video Consultation)',
      steps: [
        'Create an account.',
        'Select Video Consultation Service',
        'Upload medical reports, if any.',
        'Select a specialty or a consultant, then schedule an appointment.',
        'Complete payment to confirm your reservation',
        'Have your consultation via a secure, encrypted video call',
        'Consultation outcomes are documented in electronic medical records',
      ],
    },
    secondOpinion: {
      title: 'Second: Requesting a Second Medical Opinion',
      steps: [
        'Select Second Medical Opinion Service.',
        'Upload medical reports and imaging.',
        'Have the case reviewed by the specialized consultant.',
        'Receive the report or medical evaluation via the platform.',
      ],
    },
    warning:
      'Warning: The platform is not intended for use in medical emergencies.',
  },
  cta: {
    title: 'World-Class Quality with Local Expertise',
    description:
      'Get a medical consultation from King Faisal Specialist Hospital doctors through Takassusi Consult platform!',
    button: 'Register Now',
  },
  targetAudience: {
    title: 'Who is this service for?',
    items: [
      'For patients and their families inside or outside Saudi Arabia who are looking for a trusted reference.',
      'For those who want a second opinion for reassurance before an important treatment decision.',
      'For those who want a direct discussion with a specialist doctor about symptoms or a treatment plan.',
    ],
  },
  qualityPrivacy: {
    title: 'Quality and Privacy',
    features: [
      'Commitment to local and international health data protection standards.',
      'Secure storage of medical documents, with limited access to those who need it from the medical team.',
      'Accurate specialty matching before consultation or second medical opinion.',
    ],
    imageAlt: 'Service Quality',
  },
  faq: {
    title: 'Frequently Asked Questions',
    items: [
      {
        question: 'What is the "Takassusi Consult"?',
        answer:
          'A secure digital platform that provides access to specialized video consultations and second medical opinions from consultants at King Faisal Specialist Hospital.',
      },
      {
        question: 'What kind of services does the platform offer?',
        answer:
          'Specialized medical video consultations, review of medical reports, and second opinions for complex cases.',
      },
      {
        question: 'How to request a medical consultation?',
        answer:
          'Create an account → Select video Consultation → Upload reports → Schedule an appointment → Pay.',
      },
      {
        question: 'Can I get a second medical opinion?',
        answer:
          'Yes, by uploading medical files for evaluation by a specialist consultant.',
      },
      {
        question:
          'What medical conditions are suitable for using the platform? Does it cover emergencies?',
        answer:
          'The platform is intended for chronic or complex cases and should not be used for medical emergencies.',
      },
      {
        question: 'Is my health information safe and private on the platform',
        answer:
          'Yes, all medical information is handled with full confidentiality, and access is restricted to the relevant consultant only.',
      },
      {
        question: 'Are the physicians on the platform certified?',
        answer:
          'Yes, all physicians on the platform are licensed and certified consultants working within the King Faisal Specialist Hospital.',
      },
      {
        question: 'How long does the service take?',
        answer:
          'Second Medical Opinion: Usually, 48 to 72 business hours. Video Consultation: As per the available appointment.',
      },
      {
        question: 'Can I add family members to my account?',
        answer:
          'Yes, family members can be added with a separate medical file for each.',
      },
      {
        question:
          'Does the platform support consultations in all medical specialties?',
        answer:
          'The platform focuses on specific, precise medical specialties and is continually updated as new medical expertise becomes available.',
      },
      {
        question:
          'Is the service available only within the Kingdom or worldwide?',
        answer:
          'The service is available within the Kingdom of Saudi Arabia and internationally, provided that a suitable internet connection is available.',
      },
      {
        question: 'What payment methods are available?',
        answer:
          'Credit Cards (Visa/ MasterCard), Apple Pay, stc pay, Samsung Pay.',
      },
      {
        question:
          'Do insurance companies cover consulting services via the platform?',
        answer:
          'No, the service is not currently covered by insurance, and is paid for directly by the user.',
      },
      {
        question: 'Can I change or cancel my appointment?',
        answer:
          "Yes, appointments can be modified or canceled in accordance with the platform's modification and cancellation policy.",
      },
      {
        question:
          'What devices and technologies are required to use the platform?',
        answer:
          'A smart device or computer, a stable internet connection, a camera, and a microphone for video consultations.',
      },
    ],
  },
  footer: {
    tagline: 'Comprehensive Healthcare\nIn the Comfort of Your Home',
    quickLinks: 'Quick Links',
    contactUs: 'Contact Us',
    copyright:
      '© {year} Takassusi General Limited Company — part of King Faisal Specialist Hospital International Holding Company. All rights reserved.',
    termsAndPrivacy: 'Terms of Use and Privacy Policy',
  },
  doctors: {
    title: 'Our Doctors',
    backToHome: 'Back to Home',
    distinguishingTitle: 'What Distinguishes Our Doctors?',
    distinguishingFeatures: [
      'Consultants with extensive experience in complex and rare cases.',
      'They work with an evidence-based methodology and continuous knowledge updates.',
      'They excel at simplifying information for the patient and their family.',
    ],
    pagination: {
      previous: 'Previous page',
      next: 'Next page',
      page: 'Page {page}',
    },
  },
  common: {
    checkIconAlt: 'Check Icon',
    emailIconAlt: 'Email',
    phoneIconAlt: 'Phone',
    starIconAlt: 'star',
    arrowAlt: 'Arrow',
    chatOnWhatsapp: 'Chat with us on WhatsApp',
  },
};
